import { LoginContext } from '@aslanpay/portal-common-lib';

/**
 * Sent as x-aslan-origin on the switch-to-employee-portal onboarding calls (check-email, resend, verify)
 * so the backend resolves the employee identity directly instead of its admin-first default. Only
 * resolves EMPLOYEE (matching how mobile-app origin is treated), so this only supports switching TO the
 * employee portal.
 */
export const EMPLOYEE_PORTAL_ORIGIN = 'employee-portal';

/** The default x-aslan-origin for this app - overridden per-request by applyActiveContextOrigin
 * (see that module) to EMPLOYEE_PORTAL_ORIGIN while the active identity is EMPLOYEE, so recognition
 * audit source can distinguish an employee's actions from an admin's on this shared web app. */
export const CLIENT_PORTAL_ORIGIN = 'client-portal';

const ACTIVE_CONTEXT_STORAGE_KEY = 'aslan.active-login-context';
const SSO_CONTEXT_STORAGE_KEY = 'aslan.sso-login-context';
const SSO_PHONE_NUMBER_REQUIRED_STORAGE_KEY = 'aslan.sso-phone-number-required';

const isLoginContext = (value: string | null): value is LoginContext =>
  value === 'ADMIN' || value === 'EMPLOYEE';

export const getActiveLoginContext = (): LoginContext => {
  const value = localStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
  return isLoginContext(value) ? value : 'ADMIN';
};

export const setActiveLoginContext = (context: LoginContext) =>
  localStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, context);

export const clearActiveLoginContext = () => localStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);

export const getPendingSsoContext = (): LoginContext | undefined => {
  const value = sessionStorage.getItem(SSO_CONTEXT_STORAGE_KEY);
  return isLoginContext(value) ? value : undefined;
};

export const setPendingSsoContext = (context: LoginContext) =>
  sessionStorage.setItem(SSO_CONTEXT_STORAGE_KEY, context);

export const clearPendingSsoContext = () => sessionStorage.removeItem(SSO_CONTEXT_STORAGE_KEY);

export const setPendingSsoPhoneNumberRequired = (isRequired: boolean) => {
  if (isRequired) {
    sessionStorage.setItem(SSO_PHONE_NUMBER_REQUIRED_STORAGE_KEY, 'true');
    return;
  }

  sessionStorage.removeItem(SSO_PHONE_NUMBER_REQUIRED_STORAGE_KEY);
};

export const consumePendingSsoPhoneNumberRequired = () => {
  const isRequired = sessionStorage.getItem(SSO_PHONE_NUMBER_REQUIRED_STORAGE_KEY) === 'true';
  sessionStorage.removeItem(SSO_PHONE_NUMBER_REQUIRED_STORAGE_KEY);
  return isRequired;
};
